import { useState, useEffect, Fragment } from "react";
import {
  Popover,
  RadioGroup,
  PopoverPanel,
  PopoverButton,
  Transition,
  Description,
} from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import AppHeader from "../components/AppHeader";
import RedirectPopup from "../components/RedirectPopup";

const diagramTypes = [
  {
    id: "flowchart",
    name: "Flowchart",
    description: "Step-by-step process flow",
  },
  { id: "mindmap", name: "Mind Map", description: "Hierarchical concept map" },
  { id: "sequence", name: "Sequence", description: "Time-based interactions" },
  {
    id: "architecture",
    name: "Architecture",
    description: "System components and relationships",
  },
];

const styles = [
  { id: "modern", name: "Modern", description: "Clean and minimalist" },
  { id: "detailed", name: "Detailed", description: "Rich in information" },
  { id: "simple", name: "Simple", description: "Basic and clear" },
];

const depths = [
  { id: "low", name: "Low", description: "2 levels of depth", levels: 2 },
  { id: "medium", name: "Medium", description: "3 levels of depth", levels: 3 },
  { id: "high", name: "High", description: "4 levels of depth", levels: 4 },
];

function ConfigDropdown({ title, selected, options, onChange }) {
  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={`
              flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all duration-200
              ${
                open
                  ? "bg-indigo-50 dark:bg-indigo-900/30 ring-2 ring-indigo-500/20 dark:ring-indigo-400/30"
                  : "hover:bg-gray-50/50 dark:hover:bg-gray-800/30"
              }
            `}
          >
            <span className="text-gray-500 dark:text-gray-400">{title}:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {options.find((opt) => opt.id === selected)?.name}
            </span>
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${
                open ? "transform rotate-180" : ""
              }`}
            />
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel className="absolute z-50 mt-2 w-72 rounded-xl bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
              <RadioGroup
                value={selected}
                onChange={(value) => {
                  onChange(value);
                  close();
                }}
                className="p-2"
              >
                <div className="space-y-1">
                  {options.map((option) => (
                    <RadioGroup.Option
                      key={option.id}
                      value={option.id}
                      className={({ checked }) =>
                        `relative overflow-hidden rounded-lg p-3 cursor-pointer transition-all duration-200
                        ${
                          checked
                            ? "bg-indigo-50 dark:bg-indigo-900/30"
                            : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                        }`
                      }
                    >
                      {({ checked }) => (
                        <div className="flex flex-col text-left">
                          <div className="flex justify-between">
                            <RadioGroup.Label
                              as="p"
                              className={`font-medium ${
                                checked
                                  ? "text-indigo-900 dark:text-indigo-200"
                                  : "text-gray-900 dark:text-white"
                              }`}
                            >
                              {option.name}
                            </RadioGroup.Label>
                            {checked && (
                              <div className="text-indigo-600 dark:text-indigo-400">
                                <CheckIcon className="h-5 w-5" />
                              </div>
                            )}
                          </div>
                          <Description
                            as="span"
                            className={`text-sm ${
                              checked
                                ? "text-indigo-700 dark:text-indigo-300"
                                : "text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            {option.description}
                          </Description>
                        </div>
                      )}
                    </RadioGroup.Option>
                  ))}
                </div>
              </RadioGroup>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

function Diagram() {
  const [topic, setTopic] = useState("");
  const [diagramType, setDiagramType] = useState("flowchart");
  const [style, setStyle] = useState("modern");
  const [depth, setDepth] = useState("medium");
  const [showRedirectPopup, setShowRedirectPopup] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved
      ? JSON.parse(saved)
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const isMac = /Mac/.test(navigator.userAgent);
  const pasteShortcut = isMac ? "⌘+V" : "Ctrl+V";

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  function generatePrompt() {
    const selectedDepth = depths.find((d) => d.id === depth);
    const prompt = `Create a ${style} ${diagramType} diagram about "${topic}" with ${selectedDepth.levels} levels of depth.`;
    navigator.clipboard.writeText(prompt);
    const shouldShowPopup =
      localStorage.getItem("hideRedirectPopup") !== "true";
    setShowRedirectPopup(shouldShowPopup);
    if (!shouldShowPopup) {
      window.open("https://chatgpt.com/g/g-uefFoRnpX", "_blank");
    }
  }

  function handleRedirectConfirm(dontShowAgain) {
    if (dontShowAgain) {
      localStorage.setItem("hideRedirectPopup", "true");
    }
    setShowRedirectPopup(false);
    window.open("https://chatgpt.com/g/g-uefFoRnpX", "_blank");
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800">
      <div className="text-center fixed inset-0 p-6 md:p-10 flex flex-col gap-6 items-center justify-center max-w-4xl mx-auto">
        <AppHeader darkMode={darkMode} setDarkMode={setDarkMode} />

        <div className="w-full max-w-2xl mx-auto space-y-8">
          {/* Topic Input */}
          <div
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-sm p-6 
            border border-gray-200/50 dark:border-gray-700/50"
          >
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What would you like to create a diagram about?"
              className="w-full h-40 text-xl text-gray-900 dark:text-white bg-transparent
                focus:ring-0 focus:outline-none
                placeholder-gray-400 dark:placeholder-gray-500
                resize-none"
            />

            {/* Configuration Summary */}
            <div className="mt-4 flex flex-wrap gap-2">
              <ConfigDropdown
                title="Type"
                selected={diagramType}
                options={diagramTypes}
                onChange={setDiagramType}
              />
              <ConfigDropdown
                title="Style"
                selected={style}
                options={styles}
                onChange={setStyle}
              />
              <ConfigDropdown
                title="Depth"
                selected={depth}
                options={depths}
                onChange={setDepth}
              />
            </div>
          </div>

          {/* Generate Button */}
          <div className="h-[60px] flex items-center justify-center">
            <button
              onClick={generatePrompt}
              className={`w-full py-4 px-6 text-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 
                rounded-xl shadow-sm transition-all duration-300
                ${!topic.trim() ? "opacity-0" : "opacity-100"}`}
            >
              Generate Diagram
            </button>
          </div>
        </div>
      </div>

      <RedirectPopup
        isOpen={showRedirectPopup}
        onClose={() => setShowRedirectPopup(false)}
        onConfirm={handleRedirectConfirm}
        pasteShortcut={pasteShortcut}
      />
    </div>
  );
}

export default Diagram;
